import React from 'react';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {RootMachineContext} from '../../machines/rootMachine';
import {SmallCard} from '../../lib/small-card/small-card';
import {CardChildren} from '../../lib/card-children/card-children';
import {MeetingDateTimeInfo} from '../meeting-date-time-info/meeting-date-time-info';

import {
  selectError,
  selectIsReschedule,
  selectRescheduleLimit,
} from '../../selectors/commonSelector';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system';

import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {getReasonFormSchema} from './reason-form.schema.ts';
import {TextareaInput} from '../../lib/textarea-input';
import {z} from 'zod';
import css from './reason-form.css';


export const ReasonForm = () => {
  const {t} = useLabelTranslation();
  const rootRef = RootMachineContext.useActorRef();
  const state = RootMachineContext.useSelector((state) => state);
  const isReschedule = RootMachineContext.useSelector(selectIsReschedule);
  const error = RootMachineContext.useSelector((state) =>
    selectError(state, t),
  );
  const rescheduleLimit = RootMachineContext.useSelector(selectRescheduleLimit);

  const isCancellationSubmitting = state.matches({
    cancellationFlow: {reasonForm: 'submitting'},
  });
  const isRescheduleSubmitting = state.matches({
    rescheduleFlow: {reasonForm: 'submitting'},
  });
  const isCancellationError = state.matches({
    cancellationFlow: {reasonForm: 'error'},
  });
  const isRescheduleError = state.matches({
    rescheduleFlow: {reasonForm: 'error'},
  });
  const isError = isCancellationError || isRescheduleError;

  const reasonSchema = getReasonFormSchema(t);

  const {control, handleSubmit, formState} = useForm<
    z.infer<typeof reasonSchema>
  >({
    mode: 'onTouched',
    defaultValues: {
      reason: '',
    },
    resolver: zodResolver(reasonSchema),
  });

  const submitForm = (values: z.infer<typeof reasonSchema>) => {
    rootRef.send({type: 'reasonForm.submit', reason: values.reason || ''});
  };

  const errorText = error.errorMessage;
  const isSubmitting = isCancellationSubmitting || isRescheduleSubmitting;
  const disableSubmit = isError && !error.allowRetry;

  const showRescheduleLimit = rescheduleLimit > -1 && isReschedule;

  return (
    <SmallCard
      title={
        isReschedule
          ? t('Reschedule_Meeting', 'Reschedule Meeting')
          : t('Cancel_Meeting', 'Cancel Meeting')
      }
      icon="calendar"
    >
      <MeetingDateTimeInfo />
      {showRescheduleLimit && (
        <InContextAlert semantic={'information'}>{`${t(
          'Guest_will_get_an_email_to_reschedule_This_meeting_can_be_rescheduled_rescheduleLimit_more_times',
          {rescheduleLimit},
        )}.`}</InContextAlert>
      )}
      {isError && (
        <InContextAlert semantic="danger">
          {/* Note: already getting translated from errorText */}
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          {errorText}
        </InContextAlert>
      )}
      <CardChildren>
        <div className={css.cancelForm}>
          <TextareaInput
            label={
              isReschedule
                ? t('Reason_for_reschedule', 'Reason for reschedule')
                : t('Reason_for_cancelling', 'Reason for cancelling')
            }
            control={control}
            name="reason"
            textCountLimit={150}
            disabled={isSubmitting}
            error={Boolean(formState.errors.reason)}
            errorText={formState.errors.reason?.message}
          />

          <Button
            onClick={handleSubmit(submitForm)}
            isLoading={isSubmitting}
            disabled={!formState.isValid || disableSubmit}
          >
            {isReschedule
              ? t('Request_Reschedule', 'Request Reschedule')
              : t('Cancel', 'Cancel')}
          </Button>
        </div>
      </CardChildren>
    </SmallCard>
  );
};
